import {
  Box,
  Button,
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  DrawerProps,
  Grid,
  HStack,
  Link,
  SimpleGrid,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { SmallLogo } from "../../atoms/Logo";
import { navigate } from "gatsby";
import {
  IoCalendarClearSharp,
  MdAccessTimeFilled,
  MdChevronLeft,
  MdChevronRight,
  MdEmail,
  MdHotelClass,
  MdPhoneEnabled,
} from "../../customIcons";
import useAuth from "../../useAuth";
import { useMutation } from "@apollo/client";
import LOG_OUT from "../../../queries/LogOut";
import GET_USER from "../../../queries/getUser";
import { fetchAllBlogCategories } from "../../../queries/fetchAllBlogCategories";
import { parseHeaderBrands } from "../../utils/shopUtils";

const MobileDrawer = (props: Omit<DrawerProps, "children">) => {
  const blogs = fetchAllBlogCategories();

  const { loggedIn } = useAuth();
  const [blogIsOpen, setBlogIsOpen] = useState(false);

  const [logOut] = useMutation(LOG_OUT, {
    refetchQueries: [{ query: GET_USER }],
    onCompleted: () => {
      navigate("/sign-in");
    },
  });

  const parsedHeaderBlogs = useMemo(() => {
    const blog = parseHeaderBrands(blogs);

    return blog
      .filter(
        (item: any) =>
          item.startsWith !== "Uncategorized" &&
          item.startsWith !== "PDO Threads" &&
          item.startsWith !== "Skin Care",
      )
      .map((item: any) => ({
        ...item,
        items: item.items.filter(
          (brand: any) =>
            brand.name !== "Uncategorized" &&
            brand.name !== "PDO Threads" &&
            brand.name !== "Skin Care",
        ),
      }))
      .filter((item: any) => item.items.length > 0);
  }, [blogs]);

  useEffect(() => {
    if (blogIsOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [blogIsOpen]);

  return (
    <Drawer placement="top" {...props}>
      <DrawerOverlay />
      <DrawerContent
        justifyContent="space-between"
        h="100vh"
        bg="#BB5D82"
        color="white"
        overflowY="scroll"
      >
        <DrawerCloseButton color="white" />
        <VStack alignItems="flex-start" spacing={5} px="24px" py="16px">
          <Link href="https://www.medsupplysolutions.com">
            <SmallLogo />
          </Link>

          <Link href="https://www.medsupplysolutions.com/about-us/">
            <Button
              fontWeight="600"
              color="#fff"
              bg="#BB5D82"
              _hover={{ background: "whiteAlpha.100" }}
            >
              About
            </Button>
          </Link>
          <Link href="https://www.medsupplysolutions.com/promo/">
            <Button
              fontWeight="600"
              color="#fff"
              bg="#BB5D82"
              _hover={{ background: "whiteAlpha.100" }}
              rightIcon={<MdHotelClass />}
            >
              Promotions
            </Button>
          </Link>
          <Link href="https://www.medsupplysolutions.com/referral/">
            <Button
              fontWeight="600"
              color="#fff"
              bg="#BB5D82"
              _hover={{ background: "whiteAlpha.100" }}
            >
              Referral Program
            </Button>
          </Link>

          <Button
            color="#fff"
            fontSize="18px"
            fontWeight="500"
            variant="outline"
            borderColor="transparent"
            _hover={{ bg: "blackAlpha.200" }}
            onClick={() => navigate("/contact")}
          >
            Contact
          </Button>

          <Link
            href="https://medsupplysolutions.youcanbook.me"
            target="_blank"
            rel="noopener"
          >
            <Button
              fontWeight="600"
              variant="ghost"
              size="md"
              bg="#BB5D82"
              color="white"
              _hover={{ background: "whiteAlpha.100" }}
              rightIcon={<IoCalendarClearSharp />}
            >
              Book a Meeting
            </Button>
          </Link>

          <Box position="relative" width="100%">
            <HStack
              gap={0}
              cursor="pointer"
              onClick={(e) => {
                e.stopPropagation();
                setBlogIsOpen(!blogIsOpen);
              }}
            >
              <Button
                fontWeight="600"
                bg="#BB5D82"
                color="white"
                _hover={{ background: "transparent" }}
              >
                Blog
              </Button>

              <MdChevronRight />
            </HStack>

            {blogIsOpen && (
              <>
                <Stack
                  h="100vh"
                  position="fixed"
                  top="0"
                  left="0"
                  right="0"
                  bottom="0"
                  bg="#F8FAFC"
                  zIndex={100}
                >
                  <HStack
                    color="#1A212D"
                    px={8}
                    py={6}
                    borderBottom="#E1E7EF"
                    bg="#fff"
                    onClick={() => setBlogIsOpen(false)}
                    cursor="pointer"
                  >
                    <MdChevronLeft width="24px" height="24px" color="#3B2060" />
                    <Text fontWeight="500" fontSize="18px" pl="4">
                      Blog categories
                    </Text>
                  </HStack>
                  {blogs.length <= 30 ? (
                    <VStack
                      alignItems="flex-start"
                      py={5}
                      px={4}
                      overflowY="scroll"
                    >
                      <Link
                        color="#BB5D82"
                        fontWeight="700"
                        href="https://www.medsupplysolutions.com/blog/"
                      >
                        Visit blog page
                      </Link>
                      <SimpleGrid columns={{ base: 1 }} spacing={2}>
                        {blogs.map((blog: any) => {
                          return blog.name !== "Uncategorized" &&
                            blog.name !== "PDO Threads" &&
                            blog.name !== "Skin Care" ? (
                            <Stack key={blog.id} h="27px">
                              <Link
                                href={`https://www.medsupplysolutions.com/blog/${blog.slug}/`}
                              >
                                <Text color="#1A212D">{blog.name}</Text>
                              </Link>
                            </Stack>
                          ) : null;
                        })}
                      </SimpleGrid>
                    </VStack>
                  ) : (
                    <>
                      <Link
                        pt="4"
                        px="16px"
                        color="#BB5D82"
                        fontWeight="700"
                        href="https://www.medsupplysolutions.com/blog/"
                      >
                        Visit blog page
                      </Link>
                      <Grid
                        zIndex={100}
                        padding="16px"
                        templateColumns={{
                          base: "repeat(1, 1fr)",
                          sm: "repeat(1, 1fr)",
                          md: "repeat(2, 1fr)",
                          lg: "repeat(3, 1fr)",
                          xl: "repeat(6, 1fr)",
                        }}
                        bg="#F7FAFC"
                        gap={6}
                      >
                        {parsedHeaderBlogs?.map((item: any, index: any) => {
                          return item.name !== "Uncategorized" &&
                            item.name !== "PDO Threads" &&
                            item.name !== "Skin Care" ? (
                            <VStack key={index} alignItems="flex-start">
                              <Text
                                fontSize="14px"
                                color="#3B2060"
                                fontWeight="700"
                                textAlign="start"
                                pl="4"
                              >
                                {item.startsWith}
                              </Text>

                              <VStack
                                borderTopWidth={1}
                                borderTopColor="#E2E8F0"
                                flex={1}
                                width="100%"
                                pt="6"
                                alignItems="flex-start"
                                pl="4"
                              >
                                {item.items.map((blog: any, index: any) => {
                                  return (
                                    <Link
                                      key={index}
                                      href={`https://www.medsupplysolutions.com/blog/${blog.slug}/`}
                                    >
                                      <Text
                                        _hover={{ textDecoration: "underline" }}
                                        fontSize="18px"
                                        color="#1A212D"
                                      >
                                        {blog.name}
                                      </Text>
                                    </Link>
                                  );
                                })}
                              </VStack>
                            </VStack>
                          ) : null;
                        })}
                      </Grid>
                    </>
                  )}
                </Stack>
              </>
            )}
          </Box>

          {loggedIn && (
            <Button
              color="#fff"
              fontSize="18px"
              fontWeight="500"
              variant="outline"
              borderColor="transparent"
              _hover={{ bg: "blackAlpha.200" }}
              onClick={(e) => {
                e.stopPropagation();
                logOut();
              }}
            >
              Log out
            </Button>
          )}
        </VStack>
        <VStack alignItems="flex-start" spacing={5} p="30px" pb="80px">
          <HStack mt="20px">
            <MdPhoneEnabled w="20px" h="20px" fill="#fff" />
            <VStack ml="4px" gap={0} alignItems="flex-start">
              <Text color="#fff" fontSize="16px" fontWeight="700">
                Phone/Text
              </Text>
              <Link color="#fff" fontSize="16px">
                +1-844-226-8277
              </Link>
            </VStack>
          </HStack>
          <HStack mt="8px">
            <MdEmail w="24px" h="24px" fill="#fff" />
            <VStack ml="4px" gap={0} alignItems="flex-start">
              <Text color="#fff" fontSize="16px" fontWeight="700">
                Mail
              </Text>

              <Link
                cursor="pointer"
                href="mailto:info@medsupplysolutions.com"
                color="#fff"
                fontSize="16px"
              >
                info@medsupplysolutions.com
              </Link>
            </VStack>
          </HStack>

          <HStack gap={0} mt="24px">
            <MdAccessTimeFilled color="#fff" w="20px" h="20px" fill="#fff" />
            <VStack gap={0} ml="16px" alignItems="flex-start">
              <Text color="#fff" fontSize="16px" fontWeight="700">
                Working Hours
              </Text>
              <Text color="#fff" fontSize="14px">
                Monday to Friday: 9 AM to 6 PM EST
              </Text>
            </VStack>
          </HStack>
        </VStack>
      </DrawerContent>
    </Drawer>
  );
};

export default MobileDrawer;
